import { RocketChatStatusServiceInstance as RCStatus } from './RocketChatStatusService';
import { useWorkplaceStore } from '@/host/stores';
import { useBreakStore } from '@/host/workplace/stores/BreakStore';
import { useMultiFloorStateStore } from '@/apps/office/stores/MultiFloorStore';

/**
 * How many other people (employees + guests) are physically in the current
 * user's room right now, per the live floor-plan presence feed.
 */
const othersInCurrentRoom = (): number => {
  const { currentRoom, user } = useWorkplaceStore.getState();
  const floorState = useMultiFloorStateStore.getState().getFloorState(currentRoom.floor_pid);
  const presence = floorState?.presence;
  if (!presence || !currentRoom.pid) return 0;

  const otherUsers = presence.users.filter(
    (u) => u.room === currentRoom.pid && u.pid !== user.pid
  ).length;
  const guests = presence.guests.filter((g) => g.room === currentRoom.pid).length;
  return otherUsers + guests;
};

/**
 * Single source of truth for what the user's RC status should be right now.
 * Reads live state instead of trusting the caller's assumption about what just
 * happened, so every trigger — a real join/leave/break transition, a DDP
 * (re)connect after a refresh or network blip, or someone entering/leaving the
 * room — converges on the same answer instead of racing separate fire-and-
 * forget guesses. Busy is purely room occupancy: 2+ people (me + others) in
 * the room means busy, otherwise available — independent of any manual
 * room-lock flag, which stays a UI-only affordance.
 */
export const applyCurrentRCStatus = (): void => {
  const breakDetails = useBreakStore.getState().breakDetails;
  if (breakDetails) {
    RCStatus.onBreak(breakDetails.reason);
    return;
  }

  if (othersInCurrentRoom() >= 1) {
    RCStatus.inMeeting();
    return;
  }

  RCStatus.online();
};

// Recompute whenever the floor-plan presence feed changes — someone else
// arriving at or leaving the current room doesn't otherwise fire any of the
// existing triggers (break toggle, meeting join/leave, DDP reconnect), so
// without this the status would only ever be right by coincidence.
let lastOthersInRoom = -1;
useMultiFloorStateStore.subscribe(() => {
  const count = othersInCurrentRoom();
  if (count === lastOthersInRoom) return;
  lastOthersInRoom = count;
  applyCurrentRCStatus();
});
